import { Fragment, useEffect, useState } from "react";
import {
  Breadcrumb,
  BreadcrumbButton,
  BreadcrumbDivider,
  BreadcrumbItem,
  MessageBar,
  MessageBarBody,
  Spinner,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import { ChevronRight20Regular, Document24Regular, Folder24Regular } from "@fluentui/react-icons";
import type { DriveFolders, FileItem, FolderItem } from "@app/sp-data";
import { formatDate } from "../lib/format";
import { describeError } from "./ErrorState";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS },
  list: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "340px",
    overflowY: "auto",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    minHeight: "42px",
    padding: `0 ${tokens.spacingHorizontalM}`,
    border: "none",
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground1,
    fontFamily: "inherit",
    fontSize: tokens.fontSizeBase300,
    textAlign: "left",
    width: "100%",
    cursor: "pointer",
    flexShrink: 0,
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
  },
  selected: { backgroundColor: tokens.colorBrandBackground2, ":hover": { backgroundColor: tokens.colorBrandBackground2Hover } },
  folder: { color: tokens.colorBrandForeground1, flexShrink: 0 },
  file: { color: tokens.colorNeutralForeground3, flexShrink: 0 },
  name: { flexGrow: 1, minWidth: 0, overflowWrap: "anywhere" },
  meta: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200, whiteSpace: "nowrap" },
  empty: { padding: tokens.spacingHorizontalL, color: tokens.colorNeutralForeground3 },
});

/**
 * Scelta di un file dentro una cartella di gara (o di lotto): si naviga solo
 * all'interno di quella cartella.
 */
export function FilePicker(props: { folders: DriveFolders; root: FolderItem; selectedId?: string; onPick: (f: FileItem) => void }) {
  const s = useStyles();
  const [stack, setStack] = useState<FolderItem[]>([props.root]);
  const current = stack[stack.length - 1]!;
  const [content, setContent] = useState<{ folders: FolderItem[]; files: FileItem[] }>();
  const [error, setError] = useState<unknown>();

  useEffect(() => {
    let alive = true;
    setContent(undefined);
    setError(undefined);
    props.folders.entries(current.id).then(
      (c) => alive && setContent(c),
      (e: unknown) => alive && setError(e),
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.id]);

  return (
    <div className={s.root}>
      <Breadcrumb aria-label="Percorso" size="small">
        {stack.map((f, i) => (
          <Fragment key={f.id}>
            {i > 0 && <BreadcrumbDivider />}
            <BreadcrumbItem>
              <BreadcrumbButton current={i === stack.length - 1} onClick={() => setStack(stack.slice(0, i + 1))}>
                {f.name}
              </BreadcrumbButton>
            </BreadcrumbItem>
          </Fragment>
        ))}
      </Breadcrumb>
      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{describeError(error).detail}</MessageBarBody>
        </MessageBar>
      ) : !content ? (
        <Spinner size="small" style={{ padding: 24 }} />
      ) : (
        <div className={s.list} role="list">
          {content.folders.length + content.files.length === 0 && <div className={s.empty}>Cartella vuota.</div>}
          {content.folders.map((f) => (
            <button key={f.id} type="button" role="listitem" className={s.row} onClick={() => setStack([...stack, f])}>
              <Folder24Regular className={s.folder} />
              <span className={s.name}>{f.name}</span>
              <ChevronRight20Regular />
            </button>
          ))}
          {content.files.map((f) => (
            <button
              key={f.id}
              type="button"
              role="listitem"
              aria-pressed={f.id === props.selectedId}
              className={mergeClasses(s.row, f.id === props.selectedId && s.selected)}
              onClick={() => props.onPick(f)}
            >
              <Document24Regular className={s.file} />
              <span className={s.name}>{f.name}</span>
              {f.modifiedAt && <span className={s.meta}>{formatDate(f.modifiedAt)}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
